import { React, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";

import { PageLayout } from "./components/PageLayout";
import LoginPage from "./pages/login/LoginPage";

import { TabbedContent } from "./components/tabs/TabbedContent";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ProtectedRoute } from "./components/protectedRoute/ProtectedRoute";
import 'react-quill/dist/quill.snow.css';

const FONT_STACK = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

const theme = createTheme({
  shape: {
    borderRadius: 8,
  },
  // Compact type scale aligned to the Insurance-claims-v2 sister app (smaller menus +
  // general text). Sizes in px to mirror that app's values exactly.
  typography: {
    fontFamily: FONT_STACK,
    h1: {
      fontFamily: FONT_STACK,
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "24px",
      letterSpacing: "-0.01em",
    },
    h2: {
      fontFamily: FONT_STACK,
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: "-0.01em",
      color: "#0072E5",
    },
    h3: {
      fontFamily: FONT_STACK,
      fontSize: "15px",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: FONT_STACK,
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h5: {
      fontFamily: FONT_STACK,
      fontSize: "13px",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: FONT_STACK,
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontFamily: FONT_STACK,
      fontSize: "10px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: "#6B7A90",
    },
    body1: {
      fontFamily: FONT_STACK,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.45,
    },
    body2: {
      fontFamily: FONT_STACK,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.45,
    },
    subtitle2: {
      fontFamily: FONT_STACK,
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: 1.4,
    },
    subtitle3: {
      fontFamily: FONT_STACK,
      fontSize: "12px",
      color: "#6B7A90",
    },
    caption: {
      fontFamily: FONT_STACK,
      fontSize: "11px",
      lineHeight: 1.4,
      color: "#6B7A90",
    },
    button: {
      fontFamily: FONT_STACK,
      fontSize: "13px",
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: 0,
    },
    accordion: {
      fontFamily: FONT_STACK,
      fontSize: "12px",
      fontWeight: 400,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    link: {
      fontFamily: FONT_STACK,
      fontSize: "13px",
    },
  },
  palette: {
    primary: {
      main: "#0072E5",
      light: "#66B2FF",
      dark: "#0059B2",
      contrastText: "#fff",
    },
    secondary: {
      main: "#9C27B0",
      light: "#CE93D8",
      dark: "#7B1FA2",
      contrastText: "#fff",
    },
    error: {
      main: "#D32F2F",
    },
    warning: {
      main: "#ED6C02",
    },
    success: {
      main: "#2E7D32",
    },
    info: {
      main: "#0288D1",
      contrastText: "#fff",
    },
    grey: {
      50:  "#F3F6F9",
      100: "#E5EAF2",
      200: "#DAE2ED",
      300: "#C7D0DD",
      400: "#B0B8C4",
      500: "#9DA8B7",
      600: "#6B7A90",
      700: "#434D5B",
      800: "#303740",
      900: "#1C2025",
    },
    background: {
      default: "#F3F6F9",
      paper:   "#fff",
      // legacy keys preserved for compatibility
      main:        "#0072E5",
      active:      "#0072E5",
      menu:        "#1C2025",
      img:         "#E5EAF2",
      step:        "#DAE2ED",
      mainBlue:    "#EFF6FF",
      header:      "rgb(101, 157, 189)",
      popupHeader: "#E5EAF2",
    },
    text: {
      primary:   "#1C2025",
      secondary: "#6B7A90",
      disabled:  "#B0B8C4",
    },
    divider: "#E5EAF2",
    link: {
      main: "#0072E5",
    },
  },
  shadows: [
    "none",
    "0px 1px 2px rgba(0,0,0,0.08)",
    "0px 2px 4px rgba(0,0,0,0.08)",
    "0px 4px 8px rgba(0,0,0,0.08)",
    "0px 6px 12px rgba(0,0,0,0.08)",
    "0px 8px 16px rgba(0,0,0,0.08)",
    "0px 10px 20px rgba(0,0,0,0.08)",
    "0px 12px 24px rgba(0,0,0,0.08)",
    "0px 14px 28px rgba(0,0,0,0.08)",
    "0px 16px 32px rgba(0,0,0,0.08)",
    "0px 18px 36px rgba(0,0,0,0.08)",
    "0px 20px 40px rgba(0,0,0,0.08)",
    "0px 22px 44px rgba(0,0,0,0.08)",
    "0px 24px 48px rgba(0,0,0,0.08)",
    "0px 26px 52px rgba(0,0,0,0.08)",
    "0px 28px 56px rgba(0,0,0,0.08)",
    "0px 30px 60px rgba(0,0,0,0.08)",
    "0px 32px 64px rgba(0,0,0,0.08)",
    "0px 34px 68px rgba(0,0,0,0.08)",
    "0px 36px 72px rgba(0,0,0,0.08)",
    "0px 38px 76px rgba(0,0,0,0.08)",
    "0px 40px 80px rgba(0,0,0,0.08)",
    "0px 42px 84px rgba(0,0,0,0.08)",
    "0px 44px 88px rgba(0,0,0,0.08)",
    "0px 46px 92px rgba(0,0,0,0.08)",
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        body { background-color: #F3F6F9; }
      `,
    },
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: FONT_STACK },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "6px 16px",
          fontWeight: 700,
          fontSize: "0.8125rem",
          textTransform: "none",
          letterSpacing: 0,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        contained: {
          "&:hover": { boxShadow: "none" },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.75rem",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontSize: "12px",
          background: "#fff",
          height: "30px",
          borderRadius: 8,
          "& .trash-icon": { display: "none" },
        },
        multiline: {
          height: "auto",
          padding: "8px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#DAE2ED",
            transition: "border-color 0.15s ease",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#B0B8C4",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0072E5",
            borderWidth: "1.5px",
          },
        },
        // Compact vertical padding so content centers in the 30px root height
        input: {
          padding: "4px 10px",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontSize: "12px",
          fontWeight: 400,
          color: "#6B7A90",
          marginBottom: "4px",
          "&.Mui-focused": {
            color: "#0072E5",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            padding: "2px 8px",
            paddingRight: "32px !important",
            height: "40px",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: "#9DA8B7",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "4px 8px",
          color: "#9DA8B7",
          "&.Mui-checked": { color: "#0072E5" },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#9DA8B7",
          "&.Mui-checked": { color: "#0072E5" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontFamily: FONT_STACK,
          fontWeight: 500,
          fontSize: "0.75rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid #E5EAF2",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: "none",
        },
        elevation1: {
          boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
        },
        elevation2: {
          boxShadow: "0px 4px 16px rgba(0,0,0,0.08)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontWeight: 700,
          fontSize: "1rem",
          padding: "20px 24px 12px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "12px 24px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "12px 24px 20px",
          gap: "8px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontFamily: FONT_STACK,
            fontWeight: 700,
            fontSize: "0.75rem",
            color: "#6B7A90",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            backgroundColor: "#F3F6F9",
            borderBottom: "1px solid #E5EAF2",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontSize: "12px",
          borderColor: "#E5EAF2",
          padding: "6px 12px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
          "&:hover": { backgroundColor: "#F3F6F9" },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          "& .MuiTabs-indicator": {
            height: 2,
            borderRadius: "2px 2px 0 0",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontWeight: 600,
          fontSize: "12px",
          textTransform: "none",
          letterSpacing: 0,
          minHeight: 40,
          padding: "8px 16px",
          "&.Mui-selected": { color: "#0072E5" },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontFamily: FONT_STACK, fontSize: "12px" },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: "12px" },
        secondary: { fontSize: "11px" },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: "1px solid #E5EAF2",
          borderRadius: "8px !important",
          boxShadow: "none",
          "&:before": { display: "none" },
          "&.Mui-expanded": { margin: "8px 0" },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          fontFamily: FONT_STACK,
          fontSize: "0.75rem",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: { textTransform: "uppercase" },
        label: {
          fontFamily: FONT_STACK,
          fontWeight: 600,
          fontSize: "0.75rem",
          letterSpacing: "0.05em",
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: "#DAE2ED",
          "&.Mui-active":    { color: "#0072E5" },
          "&.Mui-completed": { color: "#2E7D32" },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: { color: "#9DA8B7" },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: FONT_STACK,
          fontSize: "12px",
        },
      },
    },
    // DataGrid (Pro) — matched to the Insurance-claims-v2 sister app: compact 12px text,
    // pale-gray header (#F1EFF5) with bold dark titles, hairline borders, no focus outline.
    // Per-grid sx still wins where a grid sets its own header colors.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontSize: "12px",
          border: "1px solid #E5EAF2",
          borderRadius: 8,
          // v9 drives the whole header (container, cells, trailing filler past the last column, and the
          // scrollbar corner) off this CSS variable — set it so the grey covers the full width.
          "--DataGrid-t-header-background-base": "#F1EFF5",
          // Belt-and-suspenders: the CSS variable above doesn't reliably reach the trailing
          // ".MuiDataGrid-filler" element (the block that fills empty space past the last column
          // when the columns don't add up to the grid's full width — used in both the header row
          // and body rows, so this is scoped to just the header's filler, not the body's).
          "& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
            backgroundColor: "#F1EFF5",
          },
        },
        columnHeaders: {
          backgroundColor: "#F1EFF5",
          color: "#211B2E",
          fontSize: "12px",
          fontWeight: 700,
          borderBottom: "1px solid #E5EAF2",
        },
        columnHeader: {
          backgroundColor: "#F1EFF5",
          "&:focus, &:focus-within": { outline: "none" },
        },
        columnHeaderTitle: { fontWeight: 700 },
        cell: {
          fontSize: "12px",
          "&:focus, &:focus-within": { outline: "none" },
        },
        row: { "&:hover": { backgroundColor: "#F3F6F9" } },
        footerContainer: { minHeight: 40 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: FONT_STACK,
          fontSize: "0.75rem",
          backgroundColor: "#1C2025",
          borderRadius: 6,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: "#E5EAF2" },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontFamily: FONT_STACK,
          fontWeight: 600,
          textTransform: "none",
          letterSpacing: 0,
          borderColor: "#DAE2ED",
          "&.Mui-selected": {
            color: "#0072E5",
            backgroundColor: "#EFF6FF",
            borderColor: "#66B2FF",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 6 },
      },
    },
  },
});

const Pages = () => {
  return <TabbedContent />;
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
// Gate the app on authentication: unauthenticated users get the branded login page
// (full-screen, no app chrome); authenticated users get the full portal.
const AppContent = () => {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  return (
    <PageLayout>
      <Pages />
    </PageLayout>
  );
};

const App = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <ThemeProvider theme={theme}>
        <AppContent />
      </ThemeProvider>
    </MsalProvider>
  );
};

export default App;
